import { Settings16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ApprovalObjectType } from 'api/notifications';
import { Button } from 'components/atoms/button';
import { CpBody01Primary } from 'components/atoms/customer-portal/typography';
import { ReactComponent as SlackLogo } from 'components/organisms/conversations/messages-list/user-message/message-origin/Slack.svg';
import { List } from 'components/organisms/notifications';
import { Header } from 'components/organisms/notifications/list/content/components';
import { RoutePaths } from 'constant';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { logger } from 'services';
import {
  handleLastSeenAtSubscription,
  usePending,
} from 'stores/notifications/use-pending';
import { useCustomerPortalHashMap } from '../../utils';

const Wrapper = styled.div`
  width: 600px;
  max-height: 500px;
  overflow-y: auto;
  max-width: 100%;

  .filter-sticky-container {
    position: sticky;
    padding-top: 0;
    background: #fff;
    top: 0;
  }
  .notifications-container {
    padding-left: var(--cp-spacing-07);
    width: 100%;
  }

  .tabs-sticky-container {
    top: unset;
    &:has(.notifications-pending) {
      padding-top: var(--cp-spacing-05);
      .notifications-pending {
        bottom: 2.25rem !important;
      }
    }
  }

  .notifications-empty-state-container {
    padding: var(--cp-spacing-05);
    .notifications-empty-state {
      width: auto;
      max-width: fit-content;
    }
  }
  ${Header.Primary} {
    flex: 0 1 75%;
  }
  ${Header.Secondary} {
    flex: 1 0 auto;
  }

  @media (max-width: 480px) {
    overflow-x: hidden;
    width: 100%;
    .notifications-container {
      padding-left: var(--cp-spacing-03);
      padding-right: var(--cp-spacing-09);
    }
    .filter-bar-secondary {
      display: none;
    }

    .filter-bar-primary {
      flex-wrap: wrap;
      width: 100%;
    }
    .filter-bar-container {
      padding: var(--cp-spacing-05) var(--cp-spacing-03);
    }
    .filter-sticky-container {
      padding-top: 0;
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--cp-spacing-03) var(--cp-spacing-07);
  padding-bottom: 0;
  padding-right: var(--cp-spacing-06);

  @media (max-width: 480px) {
    padding: var(--cp-spacing-03) var(--cp-spacing-05);
  }
`;

const TitleBarActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  ${Button} {
    pointer-events: all !important;
  }
`;

export const CpNotificationsContent: React.FC<{
  onSlackIntegrationClick?: () => void;
  onNotificationSettingsClick?: () => void;
  dontShowNotificationSettings?: boolean;
  closePopover?: () => void;
}> = ({
  onNotificationSettingsClick,
  onSlackIntegrationClick,
  dontShowNotificationSettings,
  closePopover,
}) => {
  const [pending, resetPending] = usePending();
  const history = useHistory();
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();
  const { pushToHashMap, hashMap } = useCustomerPortalHashMap();
  const router = useHistory();

  useEffect(() => {
    handleLastSeenAtSubscription();

    return () => {
      handleLastSeenAtSubscription().unsubscribe();
    };
  }, []);

  const _onSlackIconClick = useCallback(() => {
    if (dontShowNotificationSettings) {
      return;
    }
    if (onSlackIntegrationClick) {
      onSlackIntegrationClick();
    } else {
      history.push({
        pathname: RoutePaths.slackIntegration,
        hash: hashMap?.ppi ? `ppi=${hashMap?.ppi}` : '',
      });
    }
  }, [
    dontShowNotificationSettings,
    hashMap?.ppi,
    history,
    onSlackIntegrationClick,
  ]);

  const _onSettingsClick = useCallback(() => {
    if (dontShowNotificationSettings) {
      return;
    }
    if (onNotificationSettingsClick) {
      onNotificationSettingsClick();
    } else {
      history.push({
        pathname: RoutePaths.notificationsSettings,
        hash: hashMap?.ppi ? `ppi=${hashMap?.ppi}` : '',
      });
    }
  }, [
    dontShowNotificationSettings,
    hashMap?.ppi,
    history,
    onNotificationSettingsClick,
  ]);

  const slackButton = useMemo(() => {
    const button = (
      <Button type="text" icon={<SlackLogo />} onClick={_onSlackIconClick} />
    );

    if (dontShowNotificationSettings) {
      return (
        <Tooltip
          title={<FormattedMessage id="cp.slack_setttings_disabled_message" />}
        >
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }, [_onSlackIconClick, dontShowNotificationSettings]);

  const notificationSettingsButton = useMemo(() => {
    const button = (
      <Button type="text" icon={<Settings16 />} onClick={_onSettingsClick} />
    );

    if (dontShowNotificationSettings) {
      return (
        <Tooltip
          title={
            <FormattedMessage id="cp.notification_setttings_disabled_message" />
          }
        >
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }, [_onSettingsClick, dontShowNotificationSettings]);

  return (
    <Wrapper>
      <TitleWrapper>
        <CpBody01Primary>
          <FormattedMessage id="notifications" />
        </CpBody01Primary>
        <TitleBarActions>
          {slackButton}
          {notificationSettingsButton}
        </TitleBarActions>
      </TitleWrapper>
      <List
        pending={pending}
        resetPending={resetPending}
        isCustomerScreen
        callbacks={{
          onTaskNotificationClick: (g) => {
            if (openTaskDrawer) {
              const {
                task: { project, id },
              } = g;

              openTaskDrawer(project?.id, id);
              closePopover?.();
            }
          },
          onDocumentNotificationClick: (g) => {
            const {
              document: {
                tabId: spaceTabId,
                project: { id: projectId },
                space: { id: spaceId },
              },
            } = g;

            closePopover?.();

            router.push(
              RoutePaths.customerPortalSpaceTabById
                .replace(':spaceTabId', spaceTabId?.toString?.())
                .replaceAll(':spaceId', spaceId?.toString?.())
                .replaceAll(':projectId', projectId?.toString?.()),
            );
          },
          onProjectNotificationClick: () => {},
          onProjectConversationNotificationClick: () => {
            // const {
            //   projectConversation: { id },
            // } = g;
            // if (id) {
            //   pushToHashMap('cp_project_conversation_drawer', `${id}`);
            // }
          },
          onStatusUpdateNotificationClick: (g) => {
            const {
              statusUpdate: { id },
            } = g;

            if (id) {
              pushToHashMap('cp_status_update_drawer', `${id}`);
            }
            closePopover?.();
          },
          onSpaceTabNotificationClick: (g) => {
            const {
              spaceTab: { id: spaceTabId, space, project },
            } = g;

            router.push(
              RoutePaths.customerPortalSpaceTabById
                .replace(':spaceTabId', spaceTabId?.toString?.())
                .replaceAll(':spaceId', space?.id?.toString?.())
                .replaceAll(':projectId', project?.id?.toString?.()),
            );
            closePopover?.();
          },
          onApprovalNotificationClick: (g) => {
            const { approval } = g;

            switch (approval.approvalObjectType) {
              case ApprovalObjectType.Task: {
                const {
                  task: { project, id },
                } = approval;

                closePopover?.();

                openTaskDrawer(project?.id, id, TaskDrawerTab.Approvals);
                break;
              }
              case ApprovalObjectType.Document: {
                const {
                  document: { project, space, tabId },
                } = approval;

                const ppi = hashMap?.ppi;

                router.push({
                  pathname: RoutePaths.customerPortalSpaceTabById
                    .replace(':spaceTabId', tabId?.toString?.())
                    .replaceAll(':spaceId', space?.id?.toString?.())
                    .replaceAll(':projectId', project?.id?.toString?.()),
                  hash: `st_approvals=open&ppi=${ppi}`,
                });

                break;
              }
              default:
                logger.warn('unable to open group', { g });
            }
          },
        }}
      />
    </Wrapper>
  );
};
